import React, { Component } from 'react';
import Api from 'qs-services/Api';
import Modal from 'react-responsive-modal';
import PropTypes from 'prop-types';
import './BulkInventory.scss';
import { toggleGlobalLoader } from 'qs-helpers';
import { getCompanyIdFromCache } from 'qs-data-manager/Company';
import { setProductStockCount } from 'qs-data-manager/Products';
import CatalogueLib, { getActiveCatalogueId } from 'qs-data-manager/Catalogues';
import * as Sentry from '@sentry/browser';
import { getI18N } from 'qs-services/i18N';

class BulkInventory extends Component {
  constructor(props) {
    super(props);

    this.companyId = getCompanyIdFromCache();

    this.state = {
      stockAvailability: null,
      trackInventory: null,
      quantity: 1,
      autoReduce: null,
      stockVisibility: null,
      orderOnOOS: null,
      resetModalToggledFrom: null
    };
  }

  static propTypes = {
    productIds: PropTypes.array,
    toggleGlobalLoading: PropTypes.func
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { productIds } = nextProps;
    let updates = {};

    if (productIds.length !== this.props.productIds.length) {
      updates = {
        stockAvailability: null,
        trackInventory: null,
        quantity: 1,
        autoReduce: null,
        stockVisibility: null,
        orderOnOOS: null,
        resetModalToggledFrom: null
      };
    }

    if (Object.keys(updates).length) {
      this.setState(updates);
    }
  }

  changeStockAvailability = async value => {
    const { productIds } = this.props;
    const key = `changeStockAvailability${Date.now()}`;
    this.setState({ stockAvailability: !!value });

    try {
      const catalogueId = getActiveCatalogueId();
      toggleGlobalLoader(key, true);
      setProductStockCount({ stock: value, productIds });
      await Api.setBulkStockCount({ productIds, stock: value });
      CatalogueLib.getCatalogueTags(catalogueId);
      toggleGlobalLoader(key, false);
    } catch (error) {
      toggleGlobalLoader(key, false);
      Sentry.captureException(error);
    }
  };

  toggleQuantityModal = modalState => {
    this.setState({ quantityModal: modalState, quantity: 1 });
  };

  increaseInventory = () => {
    this.setState({ quantity: Number(this.state.quantity) + 1 });
  };

  onInventoryFieldChange = e => {
    const inventory = e.target.value;
    const containsDecimal = inventory.indexOf('.') >= 0;
    if (isNaN(Number(inventory)) || containsDecimal) {
      return;
    }

    this.setState({ quantity: Number(inventory) });
  };

  reduceInventory = () => {
    const currentInventory = this.state.quantity;
    if (currentInventory === 0) {
      return;
    }

    this.setState({ quantity: Number(currentInventory) - 1 });
  };

  changeTrackInventory = async trackInventory => {
    const { productIds } = this.props;
    const loaderKey = `changeTrackInventory${Date.now()}`;
    toggleGlobalLoader(loaderKey, true);

    if (trackInventory) {
      const { quantity } = this.state;
      this.toggleQuantityModal(false);
      this.changeStockAvailability(quantity);
    }
    this.setState({ trackInventory });
    await Api.setInventoryTracking({ productIds, value: trackInventory });
    toggleGlobalLoader(loaderKey, false);
  };

  changeAutoReduce = async autoReduce => {
    const { productIds } = this.props;
    const loader = `changeAutoReduce${Date.now()}`;
    const value = autoReduce ? 'VISITOR' : 'COMPANY';

    toggleGlobalLoader(loader, true);
    this.setState({ autoReduce });
    await Api.autoReduceQuantity({
      productIds,
      value
    });
    toggleGlobalLoader(loader, false);
  };

  changeStockVisibility = async stockVisibility => {
    const { productIds } = this.props;

    const loaderKey = `changeStockVisibility${Date.now()}`;
    toggleGlobalLoader(loaderKey, true);

    this.setState({ stockVisibility: stockVisibility });

    let value = null;
    if (stockVisibility === 'show') {
      value = true;
    } else if (stockVisibility === 'hide') {
      value = false;
    } else {
      value = null;
    }

    await Api.setOutOfStockVisibility({
      productIds,
      value
    });

    toggleGlobalLoader(loaderKey, false);
  };

  changeOrderOnOOS = async orderOnOOS => {
    const { productIds } = this.props;

    const loaderKey = `changeStockVisibility${Date.now()}`;
    toggleGlobalLoader(loaderKey, true);
    this.setState({ orderOnOOS });

    let value = '';
    if (orderOnOOS === 'yes') {
      value = true;
    } else if (orderOnOOS === 'no') {
      value = false;
    } else {
      value = null;
    }

    await Api.setAllowOrderOnOutOfStock({
      productIds,
      value
    });

    toggleGlobalLoader(loaderKey, false);
  };

  toggleResetModal = ({ modalState, toggledFrom }) => {
    this.setState({ showResetModal: modalState, resetModalToggledFrom: toggledFrom });
  };

  resetModal = () => {
    const { resetModalToggledFrom, showResetModal } = this.state;
    const closeModal = () => {
      this.toggleResetModal({ modalState: false, toggledFrom: null });
    };

    const { t } = getI18N();

    const reset = () => {
      if (resetModalToggledFrom === 'OrderOnOOS') {
        this.changeOrderOnOOS('default');
      } else if (resetModalToggledFrom === 'stockVisibility') {
        this.changeStockVisibility('default');
      }

      this.toggleResetModal({ modalState: false, resetModalToggledFrom: null });
    };

    return (
      <Modal
        open={showResetModal}
        onClose={closeModal}
        center
        styles={{ modal: { backgroundColor: 'white', borderRadius: 10 } }}
        showCloseIcon={false}
      >
        <div id="bulkInventoryResetModal" className="resetModalContainer">
          <div className="resetHeader">{t('reset_to_default')}</div>
          <div className="description">
            {t('this_setting_will_be_automatically_turned_on_or_off')}
          </div>

          <div className="actionButton">
            <div onClick={closeModal} className="buttonStyle">
              {t('cancel')}
            </div>
            <div onClick={reset} className="buttonStyle">
              {t('reset')}
            </div>
          </div>
        </div>
      </Modal>
    );
  };

  renderQuantityModal = () => {
    const { quantity, quantityModal } = this.state;
    const { t } = getI18N();

    const closeModal = () => {
      this.toggleQuantityModal(false);
    };

    return (
      <Modal
        open={quantityModal}
        onClose={closeModal}
        center
        styles={{ modal: { backgroundColor: 'white', borderRadius: 10 } }}
        showCloseIcon={false}
      >
        <div id="BulkInventoryQuantityModal">
          <div className="header">{t('set_inventory')}</div>
          <div className="quantityWrapper">
            <div onClick={this.reduceInventory} className="minusContainer noselect">
              -
            </div>
            <div className="quantityInputContainer">
              <input
                type="number"
                value={quantity}
                className="quantityContainer"
                onChange={this.onInventoryFieldChange}
              />
            </div>
            <div onClick={this.increaseInventory} className="plusContainer noselect">
              +
            </div>
          </div>
          <div className="buttonCotnainer">
            <div onClick={closeModal} className="cancelButton">
              {t('cancel')}
            </div>
            <div onClick={() => this.changeTrackInventory(true)} className="acceptButton">
              {t('apply')}
            </div>
          </div>
        </div>
      </Modal>
    );
  };

  render() {
    const {
      quantityModal,
      stockAvailability,
      trackInventory,
      showResetModal,
      autoReduce,
      orderOnOOS,
      stockVisibility
    } = this.state;

    const { t } = getI18N();

    return (
      <div className="bulkInventoryContainer">
        <div className="stockaAvailabilityContainer">
          <div className="rowHeading header">{t('stock_availability')}</div>
          <div className="options">
            <div onClick={() => this.changeStockAvailability(0)} className="outOfStockWrapper">
              <div
                className={
                  stockAvailability === false
                    ? 'outOfStockContainer selectedOOS'
                    : 'outOfStockContainer'
                }
              >
                {t('out_of_stock')}
              </div>
            </div>

            <div onClick={() => this.changeStockAvailability(1)} className="availableContainer">
              <div
                className={
                  stockAvailability === true
                    ? 'stockAvailableContainer availableSelected'
                    : 'stockAvailableContainer'
                }
              >
                {t('available')}
              </div>
            </div>
          </div>
        </div>

        {!!stockAvailability && (
          <div className="availableQuantityContainer">
            <div className="rowHeading header">{t('available_quantity')}</div>

            <div className="actionButtons">
              <div onClick={() => this.toggleQuantityModal(true)} className="buttonStyle">
                <div
                  className={
                    trackInventory === true
                      ? 'inventoryContainer selectedInventory'
                      : 'inventoryContainer'
                  }
                >
                  {t('track_available_quantity')}
                </div>
              </div>
              <div onClick={() => this.changeTrackInventory(false)} className="buttonStyle">
                <div
                  className={
                    trackInventory === false
                      ? 'inventoryContainer dontTrackSelected'
                      : 'inventoryContainer'
                  }
                >
                  {t('do_not_track_quantity')}
                </div>
              </div>
            </div>
          </div>
        )}

        {!!trackInventory && !!stockAvailability && (
          <div className="autoReduceInventory">
            <div className="rowHeading header">
              {t('auto_reduce_quantity_when_visitor_confirms')}
            </div>
            <div className="actionButtons">
              <div onClick={() => this.changeAutoReduce(true)} className="buttonStyle">
                <div className={autoReduce === true ? 'autoReduce selected' : 'autoReduce'}>
                  {t('yes')}
                </div>
              </div>
              <div onClick={() => this.changeAutoReduce(false)} className="buttonStyle">
                <div className={autoReduce === false ? 'autoReduce selected' : 'autoReduce'}>
                  {t('no')}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="OOSVisibilityContainer">
          <div className="rowHeading header">{t('out_of_stock_visibility')}</div>

          <div className="actionButtons">
            <div onClick={() => this.changeStockVisibility('show')} className="buttonStyle">
              <div
                className={
                  stockVisibility === 'show' ? 'stockVisibility selected' : 'stockVisibility'
                }
              >
                {t('show')}
              </div>
            </div>
            <div onClick={() => this.changeStockVisibility('hide')} className="buttonStyle">
              <div
                className={
                  stockVisibility === 'hide' ? 'stockVisibility selected' : 'stockVisibility'
                }
              >
                {t('hide')}
              </div>
            </div>
            <div
              onClick={() =>
                this.toggleResetModal({ toggledFrom: 'stockVisibility', modalState: true })
              }
              className="buttonStyle"
            >
              <div
                className={
                  stockVisibility === 'default' ? 'stockVisibility selected' : 'stockVisibility'
                }
              >
                {t('default')}
              </div>
            </div>
          </div>
        </div>

        <div className="OOSVisibilityContainer orderOnOOSContainer">
          <div className="rowHeading header">{t('allow_orders_on_out_of_stock')}</div>

          <div className="actionButtons">
            <div onClick={() => this.changeOrderOnOOS('yes')} className="buttonStyle">
              <div className={orderOnOOS === 'yes' ? 'orderOnOOS selected' : 'orderOnOOS'}>
                {t('yes')}
              </div>
            </div>
            <div onClick={() => this.changeOrderOnOOS('no')} className="buttonStyle">
              <div className={orderOnOOS === 'no' ? 'orderOnOOS selected' : 'orderOnOOS'}>
                {t('no')}
              </div>
            </div>
            <div
              onClick={() => this.toggleResetModal({ toggledFrom: 'OrderOnOOS', modalState: true })}
              className="buttonStyle"
            >
              <div className={orderOnOOS === 'default' ? 'orderOnOOS selected' : 'orderOnOOS'}>
                {t('default')}
              </div>
            </div>
          </div>
        </div>

        {quantityModal && this.renderQuantityModal()}
        {showResetModal && this.resetModal()}
      </div>
    );
  }
}

export default BulkInventory;
